import { Component, EventEmitter, Input, Output, inject, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { SearchTools } from '@omni/ai/tools/client-tools/search.tools';
import { AssistantAttachment } from '@omni/ai/features/assistant-attachment';

@Component({
  selector: 'nova-attach-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, TranslateModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './nova-attach-picker.component.html',
  styleUrls: ['./nova-attach-picker.component.scss'],
})
export class NovaAttachPickerComponent {
  /**
   * The in-scope "attach this" shortcuts shown on top. A meeting yields two
   * (the meeting + its transcript); a record yields one. Empty for the global
   * FAB chat.
   */
  @Input() contextAttachments: AssistantAttachment[] = [];
  /** Currently-attached set — used to render checkmarks on context rows. */
  @Input() attached: AssistantAttachment[] = [];
  /**
   * Emitted when the user taps a context (in-scope) row. Toggles attach/unattach.
   * The picker stays open so the user can toggle multiple items.
   */
  @Output() toggle = new EventEmitter<AssistantAttachment>();
  /**
   * Emitted when the user taps a search-result row. The host adds the item and
   * closes the picker.
   */
  @Output() pick = new EventEmitter<AssistantAttachment>();
  @Output() dismiss = new EventEmitter<void>();

  readonly #search = inject(SearchTools);
  query = '';
  results: AssistantAttachment[] = [];
  searching = false;

  /** True when the given attachment is in the currently-attached set. */
  isAttached(a: AssistantAttachment): boolean {
    return this.attached.some(x => x.id === a.id && x.kind === a.kind);
  }

  /** Stable identity for *ngFor so rows aren't destroyed/recreated every CD
   *  cycle (which dropped taps mid-interaction and broke toggle). */
  trackByKindId(_index: number, a: AssistantAttachment): string {
    return `${a.kind}:${a.id}`;
  }

  /** Icon for an attachment kind (context rows + result rows). */
  iconFor(kind: AssistantAttachment['kind']): string {
    switch (kind) {
      case 'meeting': return 'analytics-outline';
      case 'transcript': return 'document-text-outline';
      case 'account': return 'business-outline';
      case 'spotlight': return 'compass-outline';
      default: return 'person-circle-outline';
    }
  }

  /** Translation key of the short kind qualifier shown as a tag (e.g. "Analysis", "Transcript"). */
  kindTagKey(kind: AssistantAttachment['kind']): string {
    switch (kind) {
      case 'meeting': return 'NOVA_PAGE_ANALYSIS';
      case 'transcript': return 'DICTATE_TRANSCRIPT';
      case 'account': return 'ACCOUNT';
      case 'spotlight': return 'CONTACT_SPOTLIGHT';
      default: return 'CONTACT_SINGULAR';
    }
  }

  /** Translation key of the one-line muted sub-label for context rows, which clarifies what the item contains; empty when the kind has none. */
  subLabelKey(kind: AssistantAttachment['kind']): string {
    switch (kind) {
      case 'meeting': return 'NOVA_MEETING_ATTACHMENT_DETAIL';
      case 'transcript': return 'NOVA_TRANSCRIPT_ATTACHMENT_DETAIL';
      case 'spotlight': return 'NOVA_SPOTLIGHT_ATTACHMENT_DETAIL';
      default: return '';
    }
  }

  async onSearch(term: string | null | undefined): Promise<void> {
    this.query = term ?? '';
    if (!this.query.trim()) { this.results = []; return; }
    this.searching = true;
    try {
      const [c, a] = await Promise.all([
        this.#search.searchContacts(this.query, 6),
        this.#search.searchAccounts(this.query, 6),
      ]);
      const contacts: AssistantAttachment[] = (c?.contacts ?? []).map((x: any) => ({ kind: 'contact' as const, id: x.id, label: x.name }));
      const accounts: AssistantAttachment[] = (a?.accounts ?? []).map((x: any) => ({ kind: 'account' as const, id: x.id, label: x.name }));
      this.results = [...contacts, ...accounts];
    } catch {
      this.results = [];
    } finally {
      this.searching = false;
    }
  }

  pickResult(a: AssistantAttachment): void { this.pick.emit(a); }
}
